import { ActionDefinition, ColumnDefinition, FilterConfig, GridProps, RowStyleConfig } from '@sm-dashboard/types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/he';
import { Theme } from '@mui/material';
import { SxProps } from '@mui/material';
import { formatDate } from '@sm-dashboard/formatting';

dayjs.extend(utc);
dayjs.extend(timezone);

export const convertObjectToSearchParams = (object: Record<string, any>) => {
  return new URLSearchParams(Object.entries(object || {}).map(([key, value]) => [key, value?.toString() || '']));
};

export const convertSearchParamsToObject = (
  searchParams: URLSearchParams,
  filters: FilterConfig<any>[] | undefined
) => {
  if (!searchParams || !filters) return {};
  const newFilters: Record<string, any> = {};
  searchParams.forEach((value, key) => {
    const filter = filters?.find(f => f.field === key);
    if (filter) {
      switch (filter.type) {
        case 'date':
          newFilters[key] = getDateFromString(value);
          break;
        case 'hebrewDate':
          newFilters[key] = value;
          break;
        case 'number':
          newFilters[key] = Number(value);
          break;
        case 'multiSelect':
          newFilters[key] = value.split(',');
          break;
        case 'autocomplete': {
          const option = filter.options?.find(
            option => option.value?.toString() === value || option?.value?.toString()?.split('-')?.[1] === value
          );
          newFilters[key] = option ? option : value;
          break;
        }
        default:
          newFilters[key] = value;
      }
    }
  });
  return newFilters;
};

export const formatCurrency = (amount: number, currency = 'ILS') => {
  try {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: currency || 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  } catch {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }
};

export const totalPriceCalculation = (title: string, amount: number, data: any[]) => {
  let totalPrice = 0;
  data.forEach(item => {
    totalPrice += parseFloat(item.amount);
  });
  const formattedAmount = formatCurrency(amount);
  const formattedTotal = formatCurrency(totalPrice);
  return data.length > 0 ? `סה"כ ${title}: ${formattedAmount} (מוצג ${formattedTotal})` : title;
};

export const getRowStyles = <T,>(row: T, style: SxProps<Theme>, rowStyle: RowStyleConfig<T> | undefined) => {
  const { baseStyle, conditions } = rowStyle || {};
  let styles: SxProps<Theme> = { ...style };

  if (baseStyle) {
    styles = { ...styles, ...baseStyle } as SxProps<Theme>;
  }

  conditions?.forEach(condition => {
    if (condition.when(row)) {
      styles = { ...styles, ...condition.style } as SxProps<Theme>;
    }
  });

  return styles;
};

export const getToday = () => {
  return dayjs(new Date());
};

export const getOneYearAgo = () => {
  return dayjs(new Date()).subtract(1, 'year');
};

export const getStartOfMonth = () => {
  return dayjs(new Date()).startOf('month');
};

export const getDateFromString = (date: string) => {
  return dayjs(date);
};

export const getDateValue = <T,>(row: T, field: string, type: string) => {
  if (type === 'date' || type === 'dateTime') {
    return formatDate(row[field as keyof T] as string, type === 'date');
  }
  return row[field as keyof T];
};

export const getColumnWidth = <T,>(column: GridProps<T>['columns'][number], data: T[]) => {
  const { field, headerName, valueGetter, type, width } = column;

  if (width) {
    return { minWidth: width, width: width, maxWidth: width };
  }

  if (!data || !data?.length) return { minWidth: 100, width: 100, maxWidth: 150 };

  let maxLength = typeof headerName === 'string' ? headerName.length : 0;

  data?.forEach((row: T) => {
    const value = valueGetter ? valueGetter(row) : getDateValue(row, String(field), type || '');
    const length = value ? String(value).length : 0;
    if (length > maxLength) {
      maxLength = length;
    }
  });

  if (maxLength <= 7) {
    return { minWidth: 50, width: 70, maxWidth: 110 };
  }
  if (maxLength <= 20) {
    return { minWidth: 100, width: 120, maxWidth: 160 };
  }
  if (maxLength <= 35) {
    return { minWidth: 150, width: 170, maxWidth: 210 };
  }
  if (maxLength <= 45) {
    return { minWidth: 200, width: 220, maxWidth: 260 };
  }
  return { minWidth: 250, width: 270, maxWidth: 450 };
};

/**
 * Checks if a column value is empty for a given row.
 * @param row The row data
 * @param column The column definition
 * @returns True if the column value is empty, false otherwise
 */
export const isColumnValueEmpty = <T, U>(row: T | U, column: ColumnDefinition<T, U>): boolean => {
  let value: any;

  if (column.renderCell) {
    const rendered = column.renderCell(row);
    return !rendered;
  }

  if (column.valueGetter) {
    value = column.valueGetter(row);
  } else {
    value = row[column.field as keyof typeof row];
  }

  if (typeof value === 'object' && value !== null) {
    try {
      value = JSON.stringify(value);
    } catch (error) {
      return true;
    }
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return value === null || value === undefined || value === '' || value === 'null' || value === 'undefined' || !value;
};

/**
 * Filters columns based on hideIfEmpty property.
 * @param columns Array of columns to filter
 * @param row The row data
 * @param isMobile Whether to check mobileDisplayLevel instead of displayLevel
 * @returns Filtered array of columns
 */
export const filterColumnsByHideIfEmpty = <T, U>(
  columns: ColumnDefinition<T, U>[],
  row: T | U,
  isMobile = false
): ColumnDefinition<T, U>[] => {
  return columns.filter(column => {
    if (!column.hideIfEmpty) {
      return true;
    }

    if (isMobile) {
      const mobileLevel = column.mobileDisplayLevel || column.displayLevel || 'primary';
      if (mobileLevel !== 'primary' && mobileLevel !== 'first') {
        return !isColumnValueEmpty(row, column);
      }
    } else {
      const level = column.displayLevel || 'primary';
      if (level !== 'primary') {
        return !isColumnValueEmpty(row, column);
      }
    }

    return true;
  });
};

/**
 * Separates columns by their displayLevel property.
 * @param columns Array of columns to separate
 * @param isMobile Whether to use mobile display level logic
 * @returns An object with keys for each displayLevel and arrays of columns as values
 */
export const separateColumnsByDisplayLevel = <T,>(columns: ColumnDefinition<T>[], isMobile = false) => {
  if (!isMobile) {
    // Desktop logic - original behavior
    return columns
      .filter(col => col.show !== false)
      .reduce((acc, col) => {
        const level = col.displayLevel || 'primary';
        if (!acc[level]) {
          acc[level] = [];
        }
        acc[level].push(col);
        return acc;
      }, {} as Record<string, ColumnDefinition<T>[]>);
  }

  // Mobile logic - enhanced behavior with mobileDisplayLevel
  //Assign mobileDisplayLevel if missing, using displayLevel as fallback
  const updatedColumns = columns
    .filter(col => col.show !== false)
    .map((col, idx) => {
      let mobileLevel = col.mobileDisplayLevel;
      // If no mobileDisplayLevel, use displayLevel
      if (!mobileLevel) {
        if (col.displayLevel) {
          mobileLevel = col.displayLevel || 'primary';
        }
      }
      return { ...col, mobileDisplayLevel: mobileLevel };
    });

  // Assign first to the first column if no first column
  const hasFirst = updatedColumns.some(col => col.mobileDisplayLevel === 'first');

  let finalColumns = updatedColumns;
  if (!hasFirst) {
    const idx = updatedColumns.findIndex(col => col.mobileDisplayLevel === 'primary');
    if (idx !== -1) {
      finalColumns = updatedColumns.map((col, i) => (i === idx ? { ...col, mobileDisplayLevel: 'first' } : col));
    }
  }

  // Group columns by their mobileDisplayLevel
  return finalColumns.reduce((acc, col) => {
    const level = col.mobileDisplayLevel || 'primary';
    if (!acc[level]) {
      acc[level] = [];
    }
    acc[level].push(col);
    return acc;
  }, {} as Record<string, ColumnDefinition<T>[]>);
};

export const separateActionsByLevel = <T,>(actions: ActionDefinition<T>[], isMobile = false) => {
  actions?.forEach(action => {
    if (!action?.level) {
      action.level = 'regular';
    }
    if (isMobile && action?.mobileLevel) {
      action.level = action.mobileLevel;
    }
  });
  const filteredActions = actions?.filter(action => action?.level !== 'hidden');
  const primaryActions = filteredActions?.filter(action => action?.level === 'primary') || [];
  const regularActions = filteredActions?.filter(action => !action?.level || action?.level === 'regular') || [];
  return [primaryActions, regularActions];
};

/**
 * Extracts the raw value from a row for sorting purposes
 * @param row The data row
 * @param column The column definition
 * @returns The raw value to be used for sorting
 */
export const getSortValue = <T,>(row: T, column: ColumnDefinition<T>): any => {
  if (column.valueGetter) {
    return column.valueGetter(row);
  }
  const field = column.field as keyof T;
  const value = (row as any)[field];

  if (value == null && typeof field === 'string') {
    const nestedPatterns = [`Shluchim.${field}`, `Shluchim.Branch.${field}`, `Operation.${field}`, `Ticket.${field}`];

    for (const pattern of nestedPatterns) {
      const keys = pattern.split('.');
      let nestedValue = row as any;
      for (const key of keys) {
        if (nestedValue?.[key] != null) {
          nestedValue = nestedValue[key];
        } else {
          nestedValue = null;
          break;
        }
      }
      if (nestedValue != null) {
        return nestedValue;
      }
    }

    if (field === 'branchSubBranch' || field === 'shaliachBranch') {
      const branchName = (row as any)?.Shluchim?.Branch?.branchName;
      const subBranchName = (row as any)?.Shluchim?.Branch?.subBranchName;
      if (branchName || subBranchName) {
        return `${branchName || ''}${subBranchName ? ` - ${subBranchName}` : ''}`.trim();
      }
    }

    if (field === 'shaliachName') {
      const firstName = (row as any)?.Shluchim?.firstName;
      const lastName = (row as any)?.Shluchim?.lastName;
      const nickName = (row as any)?.Shluchim?.nickName;
      if (firstName || lastName || nickName) {
        return `${nickName || firstName || ''} ${lastName || ''}`.trim();
      }
    }

    if (field === 'operationId') {
      const operationName = (row as any)?.Operation?.operationName;
      if (operationName != null) {
        return operationName;
      }
    }
  }

  return value;
};

/**
 * Sorts data array by a specific column
 * @param data The data array to sort
 * @param column The column definition to sort by
 * @param direction Sort direction: 'asc' or 'desc'
 * @returns A new sorted array (immutable)
 */
export const sortDataByColumn = <T,>(data: T[], column: ColumnDefinition<T>, direction: 'asc' | 'desc'): T[] => {
  if (!data || data.length === 0 || !column) {
    return data;
  }

  // Create a new array to avoid mutating the original
  return [...data].sort((a, b) => {
    let valueA = getSortValue(a, column);
    let valueB = getSortValue(b, column);

    // Handle null/undefined values - put them at the end
    if (valueA == null && valueB == null) return 0;
    if (valueA == null) return 1;
    if (valueB == null) return -1;

    // Handle arrays - convert to string for comparison
    if (Array.isArray(valueA)) {
      valueA = valueA.join(', ');
    }
    if (Array.isArray(valueB)) {
      valueB = valueB.join(', ');
    }

    // Handle objects - convert to string for comparison
    if (typeof valueA === 'object' && valueA !== null) {
      try {
        valueA = JSON.stringify(valueA);
      } catch {
        valueA = String(valueA);
      }
    }
    if (typeof valueB === 'object' && valueB !== null) {
      try {
        valueB = JSON.stringify(valueB);
      } catch {
        valueB = String(valueB);
      }
    }

    // Handle different column types
    if (column.type === 'number' || column.type === 'amount') {
      // For amount type, remove currency symbols and parse
      let numA: number;
      let numB: number;

      if (column.type === 'amount') {
        // Remove ₪, commas, and whitespace, then parse
        const cleanA = String(valueA).replace(/[₪,\s]/g, '');
        const cleanB = String(valueB).replace(/[₪,\s]/g, '');
        numA = parseFloat(cleanA) || 0;
        numB = parseFloat(cleanB) || 0;
      } else {
        numA = typeof valueA === 'number' ? valueA : parseFloat(String(valueA)) || 0;
        numB = typeof valueB === 'number' ? valueB : parseFloat(String(valueB)) || 0;
      }

      return direction === 'asc' ? numA - numB : numB - numA;
    }

    if (column.type === 'date' || column.type === 'dateTime') {
      // Parse dates and compare timestamps
      const dateA = new Date(valueA).getTime();
      const dateB = new Date(valueB).getTime();

      // Handle invalid dates
      if (isNaN(dateA) && isNaN(dateB)) return 0;
      if (isNaN(dateA)) return 1;
      if (isNaN(dateB)) return -1;

      return direction === 'asc' ? dateA - dateB : dateB - dateA;
    }

    // String comparison (case-insensitive, with Hebrew support)
    const strA = String(valueA).toLowerCase().trim();
    const strB = String(valueB).toLowerCase().trim();

    // Use localeCompare for proper Hebrew/English sorting
    const comparison = strA.localeCompare(strB, 'he', { numeric: true, sensitivity: 'base' });

    return direction === 'asc' ? -comparison : comparison;
  });
};
/**
 * Builds a stable React key / virtualizer item key for a row, preferring `id`/`_id`
 * over the array index so windowed rows keep their identity while scrolling.
 */
export const rowStableKey = <T,>(row: T, index: number): string => {
  if (row && typeof row === 'object') {
    const o = row as Record<string, unknown>;
    if (o['id'] !== undefined && o['id'] !== null) {
      return String(o['id']);
    }
    if (o['_id'] !== undefined && o['_id'] !== null) {
      return String(o['_id']);
    }
  }
  return `i-${index}`;
};

export const mobileGridDataKey = (searchParams: URLSearchParams, excludeKeys: string[] = ['page', '_timestamp']) => {
  const copy = new URLSearchParams(searchParams);
  for (const key of excludeKeys) {
    copy.delete(key);
  }
  return copy.toString();
};
